// frontend/src/components/Dashboard/TeamMemberForm.jsx
import React, { useState, useEffect } from "react";
import { createTeamMember, updateUser } from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faPhone,
  faLock,
  faUserTag,
  faCheck,
  faTimes,
  faTractor,
  faStore,
  faUsersCog,
  faPlus,
  faCrown,
  faShieldAlt,
  faUserPlus,
  faEdit,
  faSave,
  faSpinner,
  faExclamationTriangle,
  faInfoCircle,
  faIdCard,
  faBuilding,
  faGlobe,
  faDollarSign,
  faBell,
  faUsers,
  faBriefcase,
  faPen,
  faMobileAlt,
  faAddressCard,
  faMapMarkerAlt,
  faCity,
  faLocationDot,
  faShip,
  faAnchor,
  faCheckCircle,
  faClock,
  faCalendarAlt,
  faRocket,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
  
// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// MAIN COMPONENT
// ============================================
const TeamMemberForm = ({ member, onClose, onSuccess }) => {
  const { user: currentUser, isSuperAdmin, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showFarmDetails, setShowFarmDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const isEditing = !!member;

  const [formData, setFormData] = useState({
    name: member?.name || "",
    email: member?.email || "",
    password: "",
    confirmPassword: "",
    phone: member?.phone || "",
    whatsapp: member?.whatsapp || member?.phone || "",
    role: member?.role || "customer",
    userType: member?.userType || "customer",
    isTeamMember: member?.isTeamMember || false,
    teamRole: member?.teamRole || "",
    bio: member?.bio || "",
    isActive: member?.isActive !== false,
    isVerified: member?.isVerified || false,
    preferredCurrency: member?.preferredCurrency || "SLL",
    notificationsEnabled: member?.notificationsEnabled !== false,
    emailNotifications: member?.emailNotifications !== false,
    whatsappNotifications: member?.whatsappNotifications !== false,
    addresses: member?.addresses || [],
    farmDetails: {
      farmName: member?.farmDetails?.farmName || "",
      farmSize: member?.farmDetails?.farmSize || "small",
      livestockType: member?.farmDetails?.livestockType || [],
      animalCount: member?.farmDetails?.animalCount || 0,
      yearsInOperation: member?.farmDetails?.yearsInOperation || 0,
    },
    bulkPurchaseEnabled: member?.bulkPurchaseEnabled || false,
    minimumBulkQuantity: member?.minimumBulkQuantity || 100,
    preferredPaymentMethod:
      member?.preferredPaymentMethod || "cash_on_delivery",
    creditLimit: member?.creditLimit || 0,
    creditUsed: member?.creditUsed || 0,
    paymentTerms: member?.paymentTerms || 30,
    supplierId: member?.supplierId || null,
    origin: member?.origin || SUPPLIER.country,
    supplierName: member?.supplierName || SUPPLIER.name,
  });

  const [newAddress, setNewAddress] = useState({
    recipientName: "",
    street: "",
    city: "",
    district: "",
    country: "Sierra Leone",
    postalCode: "",
    phone: "",
    isDefault: false,
    label: "Home",
  });
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);

  useEffect(() => {
    setShowFarmDetails(
      member?.role === "farmer" ||
        member?.userType === "farmer" ||
        formData.role === "farmer",
    );
  }, [member, formData.role]);

  const getRoleOptions = () => {
    const options = [
      {
        value: "customer",
        label: "Customer",
        description: "Regular customer account",
        icon: faStore,
        color: "#3b82f6",
      },
      {
        value: "farmer",
        label: "Farmer",
        description: "Bulk buyer with farm account",
        icon: faTractor,
        color: "#10b981",
      },
      {
        value: "supplier",
        label: "Supplier",
        description: "Product supplier account",
        icon: faBuilding,
        color: "#8b5cf6",
      },
    ];

    if (isAdmin() || isSuperAdmin()) {
      options.push({
        value: "admin",
        label: "Administrator",
        description: "Full access to all features",
        icon: faUsersCog,
        color: "#ef4444",
      });
    }

    if (isSuperAdmin()) {
      options.push({
        value: "super_admin",
        label: "Super Admin",
        description: "Platform owner, full system control",
        icon: faCrown,
        color: "#d9a321",
      });
    }

    return options;
  };

  const roleOptions = getRoleOptions();

  const livestockOptions = [
    "Cattle",
    "Poultry",
    "Goats",
    "Sheep",
    "Pigs",
    "Mixed",
  ];
  const farmSizeOptions = [
    { value: "small", label: "Small (1-50 animals)" },
    { value: "medium", label: "Medium (51-200 animals)" },
    { value: "large", label: "Large (200+ animals)" },
  ];
  const paymentMethodOptions = [
    { value: "cash_on_delivery", label: "Cash on Delivery" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "orange_money", label: "Orange Money" },
    { value: "afrimoney", label: "Afrimoney" },
    { value: "wave", label: "WAVE" },
    { value: "credit", label: "Credit (30 days)" },
  ];
  const currencyOptions = [
    { value: "SLL", label: "SLL (Le)" },
    { value: "USD", label: "USD ($)" },
    { value: "MYR", label: "MYR (RM)" },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else if (name.startsWith("farmDetails.")) {
      const field = name.split(".")[1];
      setFormData({
        ...formData,
        farmDetails: { ...formData.farmDetails, [field]: value },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setError("");
  };

  const handleRoleChange = (role) => {
    if (role === "super_admin" && !isSuperAdmin()) {
      setError("Only Super Admin can assign Super Admin role");
      return;
    }
    if (role === "admin" && !isAdmin() && !isSuperAdmin()) {
      setError("Only Admin or Super Admin can assign Admin role");
      return;
    }
    setFormData({
      ...formData,
      role,
      userType:
        role === "farmer"
          ? "farmer"
          : role === "supplier"
            ? "supplier"
            : role === "admin" || role === "super_admin"
              ? "customer"
              : role,
      isTeamMember:
        role === "admin" || role === "super_admin" || formData.isTeamMember,
    });
    setShowFarmDetails(role === "farmer");
    setError("");
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setNewAddress({ ...newAddress, [name]: checked });
    } else {
      setNewAddress({ ...newAddress, [name]: value });
    }
  };

  const addAddress = () => {
    if (
      !newAddress.recipientName ||
      !newAddress.street ||
      !newAddress.city ||
      !newAddress.district
    ) {
      setError("Please fill in all required address fields");
      return;
    }

    const addresses = [...formData.addresses];
    if (newAddress.isDefault) {
      addresses.forEach((addr) => (addr.isDefault = false));
    }

    if (editingAddressIndex !== null) {
      addresses[editingAddressIndex] = { ...newAddress };
      setEditingAddressIndex(null);
    } else {
      addresses.push({ ...newAddress });
    }

    setFormData({ ...formData, addresses });
    setNewAddress({
      recipientName: "",
      street: "",
      city: "",
      district: "",
      country: "Sierra Leone",
      postalCode: "",
      phone: "",
      isDefault: formData.addresses.length === 0,
      label: "Home",
    });
    setShowAddressForm(false);
  };

  const editAddress = (index) => {
    setNewAddress({ ...formData.addresses[index] });
    setEditingAddressIndex(index);
    setShowAddressForm(true);
  };

  const removeAddress = (index) => {
    const addresses = formData.addresses.filter((_, i) => i !== index);
    if (formData.addresses[index]?.isDefault && addresses.length > 0) {
      addresses[0].isDefault = true;
    }
    setFormData({ ...formData, addresses });
  };

  const handleLivestockToggle = (type) => {
    const currentTypes = formData.farmDetails.livestockType;
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];
    setFormData({
      ...formData,
      farmDetails: { ...formData.farmDetails, livestockType: newTypes },
    });
  };

  const canEditRole = () => {
    if (!isEditing) return true;
    if (member.role === "super_admin" && !isSuperAdmin()) return false;
    if (member.role === "admin" && !isAdmin() && !isSuperAdmin()) return false;
    return true;
  };

  const canEditAccount = () => {
    if (!isEditing) return true;
    if (member._id === currentUser?._id) return true;
    if (member.role === "super_admin" && !isSuperAdmin()) return false;
    if (member.role === "admin" && !isAdmin() && !isSuperAdmin()) return false;
    return true;
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = "Full name is required";
    if (!formData.email?.trim()) errors.email = "Email address is required";
    if (!isEditing) {
      if (!formData.password || formData.password.length < 6)
        errors.password = "Password must be at least 6 characters";
      if (formData.password !== formData.confirmPassword)
        errors.confirmPassword = "Passwords do not match";
    }
    if (formData.role === "farmer" && !formData.farmDetails.farmName?.trim()) {
      errors.farmName = "Farm name is required for farmer accounts";
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix all validation errors before submitting");
      return;
    }

    if (formData.role === "super_admin" && !isSuperAdmin()) {
      setError("Only Super Admin can create Super Admin accounts");
      return;
    }
    if (isEditing && member?.role === "super_admin" && !isSuperAdmin()) {
      setError("Only Super Admin can edit Super Admin accounts");
      return;
    }
    if (
      isEditing &&
      member?.role === "admin" &&
      !isAdmin() &&
      !isSuperAdmin()
    ) {
      setError("Only Admin or Super Admin can edit Admin accounts");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const submissionData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
        userType: formData.userType,
        phone: formData.phone || "",
        whatsapp: formData.whatsapp || formData.phone || "",
        isActive: formData.isActive,
        isVerified: formData.isVerified,
        preferredCurrency: formData.preferredCurrency,
        notificationsEnabled: formData.notificationsEnabled,
        emailNotifications: formData.emailNotifications,
        whatsappNotifications: formData.whatsappNotifications,
        isTeamMember: formData.isTeamMember,
        teamRole: formData.teamRole || "",
        bio: formData.bio || "",
        addresses: formData.addresses,
        bulkPurchaseEnabled: formData.bulkPurchaseEnabled,
        minimumBulkQuantity: formData.minimumBulkQuantity,
        preferredPaymentMethod: formData.preferredPaymentMethod,
        creditLimit: formData.creditLimit,
        creditUsed: formData.creditUsed,
        paymentTerms: formData.paymentTerms,
        supplierId: formData.supplierId,
        origin: formData.origin || SUPPLIER.country,
        supplierName: formData.supplierName || SUPPLIER.name,
      };

      if (!isEditing || formData.password) {
        submissionData.password = formData.password;
      }

      if (formData.role === "farmer" || formData.userType === "farmer") {
        submissionData.farmDetails = formData.farmDetails;
      }

      let response;
      if (isEditing) {
        response = await updateUser(member._id, submissionData);
      } else {
        response = await createTeamMember(submissionData);
      }

      if (response.data.success) {
        setSuccess(
          isEditing
            ? "Team member updated successfully!"
            : "Team member created successfully!",
        );
        setTimeout(() => {
          if (onSuccess) onSuccess(response.data.data);
          if (onClose) onClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Error saving team member:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Error saving team member. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderBasicInfo = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faUser} /> Basic Information
      </h3>
      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faUser} /> Full Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="e.g., John Doe"
            disabled={!canEditAccount()}
          />
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faEnvelope} /> Email Address *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="e.g., john@kenalienterprise.com"
            disabled={isEditing}
          />
        </div>
      </div>

      {!isEditing && (
        <div className="form-row">
          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faLock} /> Password *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Minimum 6 characters"
              disabled={!canEditAccount()}
            />
          </div>
          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faLock} /> Confirm Password *
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="Re-enter password"
              disabled={!canEditAccount()}
            />
          </div>
        </div>
      )}

      {isEditing && (
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faLock} /> New Password (Optional)
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Leave blank to keep current password"
            disabled={!canEditAccount()}
          />
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faPhone} /> Phone Number
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="e.g., +232 76 123 456"
            disabled={!canEditAccount()}
          />
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Number
          </label>
          <input
            type="tel"
            name="whatsapp"
            value={formData.whatsapp}
            onChange={handleChange}
            placeholder="e.g., +232 76 123 456"
            disabled={!canEditAccount()}
          />
          <small style={{ color: "#6B7280", fontSize: "0.65rem" }}>
            For customer support communication
          </small>
        </div>
      </div>

      <div className="form-group">
        <label>
          <FontAwesomeIcon icon={faPen} /> Bio / Description
        </label>
        <textarea
          name="bio"
          value={formData.bio}
          onChange={handleChange}
          rows="2"
          placeholder="Brief description or bio for team members"
          disabled={!canEditAccount()}
        />
      </div>

      <div
        className="supplier-origin-info"
        style={{
          background: "#e8f5e9",
          padding: "0.5rem 0.75rem",
          borderRadius: "8px",
          marginTop: "0.5rem",
        }}
      >
        <FontAwesomeIcon icon={faShip} style={{ color: "#006B2E" }} />
        <span
          style={{
            fontSize: "0.75rem",
            color: "#006B2E",
            marginLeft: "0.5rem",
          }}
        >
          Products sourced from <strong>{SUPPLIER.name}</strong> (
          {SUPPLIER.country})
        </span>
      </div>
    </div>
  );

  const renderRoleAccess = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faUserTag} /> Role & Access
      </h3>
      <div className="role-selection">
        {roleOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`role-option ${formData.role === option.value ? "active" : ""}`}
            onClick={() => handleRoleChange(option.value)}
            disabled={!canEditRole() || !canEditAccount()}
            style={{
              borderColor:
                formData.role === option.value ? option.color : "#e5e5e5",
            }}
          >
            <div className="role-icon" style={{ color: option.color }}>
              <FontAwesomeIcon icon={option.icon} />
            </div>
            <div className="role-info">
              <div className="role-label" style={{ color: option.color }}>
                {option.label}
              </div>
              <div className="role-description">{option.description}</div>
            </div>
            {formData.role === option.value && (
              <div
                className="role-check"
                style={{ backgroundColor: option.color }}
              >
                <FontAwesomeIcon icon={faCheck} />
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faBriefcase} /> Team Role Title
          </label>
          <input
            type="text"
            name="teamRole"
            value={formData.teamRole}
            onChange={handleChange}
            placeholder="e.g., Operations Manager"
            disabled={!canEditAccount()}
          />
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faDollarSign} /> Preferred Currency
          </label>
          <select
            name="preferredCurrency"
            value={formData.preferredCurrency}
            onChange={handleChange}
            disabled={!canEditAccount()}
          >
            {currencyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="isTeamMember"
            checked={formData.isTeamMember}
            onChange={handleChange}
            disabled={!canEditAccount()}
          />
          <span>
            <FontAwesomeIcon icon={faShieldAlt} /> Team Member
          </span>
          <small
            style={{ display: "block", color: "#6B7280", marginTop: "0.25rem" }}
          >
            Grant staff access to team features
          </small>
        </label>
      </div>
    </div>
  );

  const renderFarmDetails = () => (
    <div className="form-section farmer-details">
      <h3>
        <FontAwesomeIcon icon={faTractor} /> Farm Details
      </h3>
      <div className="form-group">
        <label>Farm/Business Name *</label>
        <input
          type="text"
          name="farmDetails.farmName"
          value={formData.farmDetails.farmName}
          onChange={handleChange}
          required
          placeholder="e.g., Green Valley Farm"
          disabled={!canEditAccount()}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Farm Size</label>
          <select
            name="farmDetails.farmSize"
            value={formData.farmDetails.farmSize}
            onChange={handleChange}
            disabled={!canEditAccount()}
          >
            {farmSizeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Animal Count</label>
          <input
            type="number"
            name="farmDetails.animalCount"
            value={formData.farmDetails.animalCount}
            onChange={handleChange}
            min="0"
            disabled={!canEditAccount()}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Livestock Type</label>
        <div className="livestock-tags">
          {livestockOptions.map((type) => (
            <button
              key={type}
              type="button"
              className={`livestock-tag ${formData.farmDetails.livestockType.includes(type) ? "active" : ""}`}
              onClick={() => handleLivestockToggle(type)}
              disabled={!canEditAccount()}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Years in Operation</label>
          <input
            type="number"
            name="farmDetails.yearsInOperation"
            value={formData.farmDetails.yearsInOperation}
            onChange={handleChange}
            min="0"
            disabled={!canEditAccount()}
          />
        </div>
        <div className="form-group">
          <label>Preferred Payment Method</label>
          <select
            name="preferredPaymentMethod"
            value={formData.preferredPaymentMethod}
            onChange={handleChange}
            disabled={!canEditAccount()}
          >
            {paymentMethodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Bulk Purchase Enabled</label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="bulkPurchaseEnabled"
              checked={formData.bulkPurchaseEnabled}
              onChange={handleChange}
              disabled={!canEditAccount()}
            />
            Enable bulk purchases
          </label>
        </div>
        <div className="form-group">
          <label>Minimum Bulk Quantity (kg)</label>
          <input
            type="number"
            name="minimumBulkQuantity"
            value={formData.minimumBulkQuantity}
            onChange={handleChange}
            min="0"
            disabled={!canEditAccount()}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Credit Limit (USD)</label>
          <input
            type="number"
            name="creditLimit"
            value={formData.creditLimit}
            onChange={handleChange}
            min="0"
            step="0.01"
            disabled={!canEditAccount()}
          />
        </div>
        <div className="form-group">
          <label>Credit Used (USD)</label>
          <input
            type="number"
            name="creditUsed"
            value={formData.creditUsed}
            onChange={handleChange}
            min="0"
            step="0.01"
            disabled={!canEditAccount()}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Payment Terms (days)</label>
        <input
          type="number"
          name="paymentTerms"
          value={formData.paymentTerms}
          onChange={handleChange}
          min="0"
          disabled={!canEditAccount()}
        />
      </div>

      <div
        className="supplier-info"
        style={{
          background: "#f0f9ff",
          padding: "0.5rem 0.75rem",
          borderRadius: "8px",
          marginTop: "0.5rem",
        }}
      >
        <FontAwesomeIcon icon={faGlobe} style={{ color: "#3b82f6" }} />
        <span
          style={{
            fontSize: "0.75rem",
            color: "#1f1f1f",
            marginLeft: "0.5rem",
          }}
        >
          Products sourced from <strong>{SUPPLIER.name}</strong> (
          {SUPPLIER.country})
        </span>
      </div>
    </div>
  );

  const renderAddresses = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faIdCard} /> Addresses
      </h3>
      <div className="addresses-list">
        {formData.addresses.map((address, index) => (
          <div key={index} className="address-item">
            <div className="address-info">
              <div className="address-label">
                {address.label || "Address"}{" "}
                {address.isDefault && (
                  <span className="default-badge">Default</span>
                )}
              </div>
              <div className="address-details">
                {address.recipientName}
                <br />
                {address.street}
                <br />
                {address.city}, {address.district}
                <br />
                {address.country}
                {address.phone && (
                  <>
                    <br />
                    <FontAwesomeIcon icon={faPhone} /> {address.phone}
                  </>
                )}
              </div>
            </div>
            <div className="address-actions">
              <button
                type="button"
                className="edit-btn"
                onClick={() => editAddress(index)}
                disabled={!canEditAccount()}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                type="button"
                className="remove-btn"
                onClick={() => removeAddress(index)}
                disabled={!canEditAccount() || formData.addresses.length <= 1}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {!showAddressForm ? (
        <button
          type="button"
          className="add-address-btn"
          onClick={() => {
            setShowAddressForm(true);
            setEditingAddressIndex(null);
            setNewAddress({
              recipientName: formData.name || "",
              street: "",
              city: "",
              district: "",
              country: "Sierra Leone",
              postalCode: "",
              phone: formData.phone || "",
              isDefault: formData.addresses.length === 0,
              label: "Home",
            });
          }}
          disabled={!canEditAccount()}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Address
        </button>
      ) : (
        <div className="address-form">
          <h4>
            {editingAddressIndex !== null ? "Edit Address" : "Add New Address"}
          </h4>
          <div className="form-row">
            <div className="form-group">
              <label>Recipient Name *</label>
              <input
                type="text"
                name="recipientName"
                value={newAddress.recipientName}
                onChange={handleAddressChange}
                placeholder="Recipient name"
              />
            </div>
            <div className="form-group">
              <label>Label</label>
              <input
                type="text"
                name="label"
                value={newAddress.label}
                onChange={handleAddressChange}
                placeholder="e.g., Home, Office"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Street Address *</label>
            <input
              type="text"
              name="street"
              value={newAddress.street}
              onChange={handleAddressChange}
              placeholder="Street address"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={newAddress.city}
                onChange={handleAddressChange}
                placeholder="City"
              />
            </div>
            <div className="form-group">
              <label>District *</label>
              <input
                type="text"
                name="district"
                value={newAddress.district}
                onChange={handleAddressChange}
                placeholder="District"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                name="country"
                value={newAddress.country}
                onChange={handleAddressChange}
                placeholder="Country"
              />
            </div>
            <div className="form-group">
              <label>Postal Code</label>
              <input
                type="text"
                name="postalCode"
                value={newAddress.postalCode}
                onChange={handleAddressChange}
                placeholder="Postal code"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              name="phone"
              value={newAddress.phone}
              onChange={handleAddressChange}
              placeholder="Phone number"
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isDefault"
                checked={newAddress.isDefault}
                onChange={handleAddressChange}
              />
              Set as default address
            </label>
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowAddressForm(false);
                setEditingAddressIndex(null);
              }}
            >
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={addAddress}>
              <FontAwesomeIcon icon={faCheck} /> Save Address
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderAccountSettings = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faShieldAlt} /> Account Settings
      </h3>
      <div className="form-row">
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              disabled={!canEditAccount()}
            />
            <span>
              <FontAwesomeIcon icon={faCheck} /> Active Account
            </span>
            <small
              style={{
                display: "block",
                color: "#6B7280",
                marginTop: "0.25rem",
              }}
            >
              User can log in immediately
            </small>
          </label>
        </div>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isVerified"
              checked={formData.isVerified}
              onChange={handleChange}
              disabled={!canEditAccount()}
            />
            <span>
              <FontAwesomeIcon icon={faCheck} /> Email Verified
            </span>
            <small
              style={{
                display: "block",
                color: "#6B7280",
                marginTop: "0.25rem",
              }}
            >
              User's email has been verified
            </small>
          </label>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="notificationsEnabled"
              checked={formData.notificationsEnabled}
              onChange={handleChange}
              disabled={!canEditAccount()}
            />
            <span>
              <FontAwesomeIcon icon={faBell} /> Enable Notifications
            </span>
            <small
              style={{
                display: "block",
                color: "#6B7280",
                marginTop: "0.25rem",
              }}
            >
              Send notifications to this user
            </small>
          </label>
        </div>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="emailNotifications"
              checked={formData.emailNotifications}
              onChange={handleChange}
              disabled={!canEditAccount()}
            />
            <span>
              <FontAwesomeIcon icon={faEnvelope} /> Email Notifications
            </span>
            <small
              style={{
                display: "block",
                color: "#6B7280",
                marginTop: "0.25rem",
              }}
            >
              Send email notifications to this user
            </small>
          </label>
        </div>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="whatsappNotifications"
            checked={formData.whatsappNotifications}
            onChange={handleChange}
            disabled={!canEditAccount()}
          />
          <span>
            <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Notifications
          </span>
          <small
            style={{ display: "block", color: "#6B7280", marginTop: "0.25rem" }}
          >
            Send WhatsApp notifications to this user
          </small>
        </label>
      </div>

      {formData.role === "supplier" && (
        <div className="form-group">
          <label>Supplier ID</label>
          <input
            type="text"
            name="supplierId"
            value={formData.supplierId || ""}
            onChange={handleChange}
            placeholder="Supplier reference ID"
            disabled={!canEditAccount()}
          />
        </div>
      )}

      <div
        className="supplier-origin-info"
        style={{
          background: "#f0f9ff",
          padding: "0.5rem 0.75rem",
          borderRadius: "8px",
          marginTop: "0.5rem",
        }}
      >
        <FontAwesomeIcon icon={faAnchor} style={{ color: "#3b82f6" }} />
        <span
          style={{
            fontSize: "0.75rem",
            color: "#1f1f1f",
            marginLeft: "0.5rem",
          }}
        >
          Default supplier: <strong>{SUPPLIER.name}</strong> ({SUPPLIER.country}
          )
        </span>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "basic":
        return renderBasicInfo();
      case "role":
        return renderRoleAccess();
      case "farm":
        return showFarmDetails ? (
          renderFarmDetails()
        ) : (
          <div className="info-message">
            <FontAwesomeIcon icon={faInfoCircle} />
            <p>
              This user is not a farmer. Switch to Farmer role to see farm
              details.
            </p>
          </div>
        );
      case "addresses":
        return renderAddresses();
      case "settings":
        return renderAccountSettings();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {isEditing ? (
              <>
                <FontAwesomeIcon icon={faEdit} /> Edit Team Member
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUserPlus} /> Add Team Member
              </>
            )}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <p className="modal-subtitle">
          {isEditing
            ? `Edit ${member?.name}'s account details for Kenali Enterprise`
            : "Create a new staff or customer account for Kenali Enterprise"}
          <span className="supplier-tag">
            <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
          </span>
        </p>

        {error && (
          <div className="alert-message error">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}
        {success && (
          <div className="alert-message success">
            <FontAwesomeIcon icon={faCheck} /> {success}
          </div>
        )}

        <div className="form-tabs">
          <button
            className={activeTab === "basic" ? "active" : ""}
            onClick={() => setActiveTab("basic")}
          >
            <FontAwesomeIcon icon={faUser} /> Basic
          </button>
          <button
            className={activeTab === "role" ? "active" : ""}
            onClick={() => setActiveTab("role")}
          >
            <FontAwesomeIcon icon={faUserTag} /> Role
          </button>
          {showFarmDetails && (
            <button
              className={activeTab === "farm" ? "active" : ""}
              onClick={() => setActiveTab("farm")}
            >
              <FontAwesomeIcon icon={faTractor} /> Farm
            </button>
          )}
          <button
            className={activeTab === "addresses" ? "active" : ""}
            onClick={() => setActiveTab("addresses")}
          >
            <FontAwesomeIcon icon={faIdCard} /> Addresses
          </button>
          <button
            className={activeTab === "settings" ? "active" : ""}
            onClick={() => setActiveTab("settings")}
          >
            <FontAwesomeIcon icon={faShieldAlt} /> Settings
          </button>
        </div>

        <form onSubmit={handleSubmit} className="team-member-form">
          {renderContent()}

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !canEditAccount()}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                </>
              ) : isEditing ? (
                <>
                  <FontAwesomeIcon icon={faSave} /> Update Member
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUserPlus} /> Create Member
                </>
              )}
            </button>
          </div>
        </form>

        {isEditing && member?._id === currentUser?._id && (
          <div className="modal-footer-note warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <small>
              You are editing your own account. Be careful with role changes.
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMemberForm;
